import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, SummarizationNamespaces } from '@/app/utils/constants';
import { ContractsIcon, FileDownloadIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import useExportReport from '@/app/components/shared/ExportButton/useExportReport';
import { ResourceConfig } from '@/app/components/features/evaluations/ResourceView/config';
import { SummaryPageContent } from '@/app/components/features/evaluations/Summary/SummaryPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSuspenseQuery } from '@tanstack/react-query';
import { validator } from '@/app/validator';
import z from 'zod';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(main)/summary',
)({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      targetStatementId: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const tGeneral = useTranslations('general');
  const tFeature = useTranslations('features.summary');
  const t = useTranslations('pages.contractHome');
  const navigate = useNavigate();
  const config = ResourceConfig['contract'];

  const { contractId, projectId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const { exportMutation } = useExportReport(config.reportType, contractId);

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderButton
          aria-label={tGeneral('generateReport')}
          pending={exportMutation.isPending}
          onClick={() => exportMutation.mutate(contractId)}
        >
          <HugeiconsIcon icon={FileDownloadIcon} />
        </HeaderButton>
        <HeaderSidechatButton />
      </Header>
      <SummaryPageContent
        evaluationId={contractId}
        summaryType={SummarizationNamespaces.CONTRACT}
        reportType={config.reportType}
      />
    </>
  );
}
